/**
 * =============================================================================
 * USE REPLIES HOOK
 * =============================================================================
 *
 * Hook for managing reply data and moderation actions.
 * Follows the QuestionQueue pattern for optimistic updates.
 *
 * GAP-008: Admin Reply & Solution Management UI
 *
 * =============================================================================
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { adminApi } from '../api/client';
import { useToast } from './useToast';
import { CONTENT_VALIDATION, QUESTION_STATUSES } from '@find-my-part/types';
import type {
  AdminReplyListItem,
  PaginationMeta,
  QuestionStatus,
  ReplyModerationStatus,
} from '@find-my-part/types';
import {
  isAmbiguousSolutionFailure,
  readQuestionSolutionConflict,
  type SolutionWriteIssue,
} from '../components/Questions/SolutionWriteNotice';

/**
 * Options for the useReplies hook.
 */
export interface UseRepliesOptions {
  /** Question ID to fetch replies for */
  questionId: string;
  /** Auth token */
  token: string;
  /** Current question-status baseline used by guarded solution marks */
  questionStatus: string;
  /** Whether to auto-fetch on mount */
  enabled?: boolean;
  /**
   * Invoked with the created reply's id after a SUCCESSFUL staff-reply create
   * (PR4b — the post-create image-attach affordance needs the new replyId,
   * which the boolean contract discards). Called before the reconciling
   * refetch; optional so existing callers are untouched.
   */
  onCreated?: (replyId: string) => void;
}

/**
 * Return value of the useReplies hook.
 */
export interface UseRepliesResult {
  /** List of replies */
  replies: AdminReplyListItem[];
  /** Pagination metadata */
  pagination: PaginationMeta | null;
  /** Whether replies are loading */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Set of reply IDs with pending actions */
  pendingActions: Set<string>;
  /** Whether a staff reply create is in flight */
  isCreatingStaffReply: boolean;
  /** Whether any solution mark/unmark or recovery sequence is in flight */
  isSolutionActionInFlight: boolean;
  /** Reply whose solution action owns the loading indicator */
  solutionPendingReplyId: string | null;
  /** Persistent guarded-write recovery state */
  solutionWriteIssue: SolutionWriteIssue | null;
  /** Current page number */
  page: number;
  /** Navigate to a page */
  setPage: (page: number) => void;
  /** Refetch replies from API */
  refetch: () => Promise<void>;
  /**
   * Create a staff-authored (admin) reply. Returns true on success (composer
   * should clear), false on validation/API/network failure (composer should
   * preserve its text). On success, replies are refetched from the server —
   * no synthetic reply row is inserted.
   */
  createStaffReply: (body: string) => Promise<boolean>;
  /** Approve a reply */
  approveReply: (replyId: string) => Promise<void>;
  /** Reject a reply (requires reason) */
  rejectReply: (replyId: string, reason: string) => Promise<void>;
  /** Hide a reply */
  hideReply: (replyId: string) => Promise<void>;
  /** Mark a reply as solution */
  markSolution: (replyId: string) => Promise<void>;
  /** Unmark a reply as solution */
  unmarkSolution: (replyId: string) => Promise<void>;
  /** Refresh/reapply or confirm the preserved solution write */
  recoverSolutionWrite: () => Promise<void>;
  /** Dismiss the persistent solution write notice */
  dismissSolutionWriteIssue: () => void;
}

/**
 * User-friendly error messages for common error codes.
 */
const ERROR_MESSAGES: Record<string, string> = {
  NOT_FOUND: 'Reply not found',
  INVALID_STATE_TRANSITION: 'Action not allowed for this reply',
  VALIDATION_ERROR: 'Invalid request',
  UNAUTHORIZED: 'Session expired. Please refresh the page.',
  // Shared/default across ALL reply actions (approve/reject/hide/solution/
  // staff-create) — keep generic. The staff-create path overrides this with a
  // context-specific message inside createStaffReply.
  FORBIDDEN: 'You do not have permission to perform this reply action.',
};

/** Context-specific message for a FORBIDDEN response when authoring a staff reply. */
const STAFF_REPLY_FORBIDDEN_MESSAGE = 'You do not have permission to post staff replies.';

function getUserFriendlyMessage(code: string, fallback: string): string {
  return ERROR_MESSAGES[code] ?? fallback;
}

/**
 * Captures the state of a single reply before an optimistic update,
 * so rollback only affects the targeted row — not the entire list.
 */
function captureReplyState(
  replies: AdminReplyListItem[],
  replyId: string
): AdminReplyListItem | undefined {
  return replies.find(r => r.replyId === replyId);
}

/**
 * Restores a single reply's state without affecting other rows.
 * This prevents cross-row corruption when concurrent actions overlap.
 */
function rollbackSingleReply(
  prev: AdminReplyListItem[],
  savedReply: AdminReplyListItem
): AdminReplyListItem[] {
  return prev.map(r =>
    r.replyId === savedReply.replyId ? savedReply : r
  );
}

/**
 * For solution mark, we also need to rollback the previously-unmarked
 * solutions. This captures all replies that had isSolution=true before
 * the optimistic toggle so we can restore them on failure.
 */
type SolutionStateSnapshot = Map<string, boolean>;

function captureSolutionState(replies: AdminReplyListItem[]): SolutionStateSnapshot {
  return new Map(replies.map((reply) => [reply.replyId, reply.isSolution]));
}

function rollbackSolutionState(
  prev: AdminReplyListItem[],
  snapshot: SolutionStateSnapshot
): AdminReplyListItem[] {
  return prev.map((reply) =>
    snapshot.has(reply.replyId)
      ? { ...reply, isSolution: snapshot.get(reply.replyId) ?? false }
      : reply
  );
}

function readQuestionStatus(value: unknown): QuestionStatus | null {
  return typeof value === 'string' &&
    (QUESTION_STATUSES as readonly string[]).includes(value)
    ? (value as QuestionStatus)
    : null;
}

/**
 * Hook for managing reply data and moderation actions.
 *
 * Follows the QuestionQueue pattern:
 * - Fetches data on mount when enabled
 * - Tracks pending actions per reply (prevents concurrent actions on same row)
 * - Allows concurrent actions across different rows
 * - Applies optimistic updates for immediate UI feedback
 * - Rolls back only the affected row on failure (safe for concurrent actions)
 * - Handles no-op responses (applied: false) gracefully
 * - Refetches after solution mark/unmark to reconcile server truth
 * - Integrates with useToast for notifications
 * - Protected against setState after unmount
 *
 * @param options - Configuration options
 * @returns Reply data and action handlers
 *
 * @example
 * ```tsx
 * const { replies, isLoading, approveReply } = useReplies({
 *   questionId: 'q_abc123',
 *   token: authToken,
 *   questionStatus: 'OPEN',
 * });
 * ```
 */
export function useReplies({
  questionId,
  token,
  questionStatus,
  enabled = true,
  onCreated,
}: UseRepliesOptions): UseRepliesResult {
  const [replies, setReplies] = useState<AdminReplyListItem[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingActions, setPendingActions] = useState<Set<string>>(new Set());
  const [isCreatingStaffReply, setIsCreatingStaffReply] = useState(false);
  const [isSolutionActionInFlight, setIsSolutionActionInFlight] = useState(false);
  const [solutionPendingReplyId, setSolutionPendingReplyId] = useState<string | null>(null);
  const [solutionWriteIssue, setSolutionWriteIssue] = useState<SolutionWriteIssue | null>(null);
  const repliesRef = useRef(replies);
  repliesRef.current = replies;
  const solutionActionInFlightRef = useRef(false);
  // Recovery may hydrate an adjacent page before adopting its page number.
  // Suppress exactly that next page-effect fetch so a redundant request cannot
  // replace the already-verified recovery state with an unrelated error.
  const solutionHydratedPageRef = useRef<number | null>(null);
  const solutionStatusBaselineRef = useRef<QuestionStatus | null>(
    readQuestionStatus(questionStatus)
  );
  const solutionWriteEventSequenceRef = useRef(0);
  // Synchronous in-flight guard for staff reply creation. React state updates
  // are async, so two invocations in the same tick can both read a stale
  // `isCreatingStaffReply === false`; this ref flips synchronously so the
  // second invocation is rejected before it can issue a duplicate POST. The
  // `isCreatingStaffReply` STATE remains the source of truth for UI rendering.
  const isCreatingStaffReplyRef = useRef(false);
  const [page, setPage] = useState(1);
  const { showSuccess, showError } = useToast();

  // Unmount protection: prevents setState calls after component is destroyed
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Track the current questionId to prevent stale fetch results
  // and reset page to 1 when switching questions
  const questionIdRef = useRef(questionId);
  useEffect(() => {
    if (questionIdRef.current !== questionId) {
      questionIdRef.current = questionId;
      solutionHydratedPageRef.current = null;
      setPage(1);
      setSolutionWriteIssue(null);
      solutionStatusBaselineRef.current = readQuestionStatus(questionStatus);
    }
  }, [questionId, questionStatus]);

  // A parent question refresh supplies a newer load-time status baseline. A
  // solution success/conflict also updates this ref locally below, so a stale
  // unchanged prop cannot overwrite the newer baseline between those events.
  useEffect(() => {
    const nextStatus = readQuestionStatus(questionStatus);
    if (nextStatus) solutionStatusBaselineRef.current = nextStatus;
  }, [questionStatus]);

  const addPending = useCallback((replyId: string) => {
    setPendingActions(prev => new Set(prev).add(replyId));
  }, []);

  const removePending = useCallback((replyId: string) => {
    setPendingActions(prev => {
      const next = new Set(prev);
      next.delete(replyId);
      return next;
    });
  }, []);

  const publishSolutionWriteIssue = useCallback((issue: SolutionWriteIssue): void => {
    const eventId = issue.eventId ?? ++solutionWriteEventSequenceRef.current;
    const announcementKind = issue.announcementKind ?? issue.kind;
    setSolutionWriteIssue({ ...issue, eventId, announcementKind });
  }, []);

  const dismissSolutionWriteIssue = useCallback((): void => {
    if (solutionActionInFlightRef.current) return;
    setSolutionWriteIssue(null);
  }, []);

  /**
   * Claim the panel-wide solution slot synchronously. React state is only the
   * rendering mirror; the ref prevents two reply controls activated in one
   * tick from issuing competing requests.
   */
  const claimSolutionAction = useCallback(
    (replyId: string): boolean => {
      if (solutionActionInFlightRef.current || pendingActions.has(replyId)) return false;
      solutionActionInFlightRef.current = true;
      setIsSolutionActionInFlight(true);
      setSolutionPendingReplyId(replyId);
      addPending(replyId);
      return true;
    },
    [addPending, pendingActions]
  );

  const releaseSolutionAction = useCallback(
    (replyId: string): void => {
      solutionActionInFlightRef.current = false;
      if (!isMountedRef.current) return;
      setIsSolutionActionInFlight(false);
      setSolutionPendingReplyId(null);
      removePending(replyId);
    },
    [removePending]
  );

  const fetchReplies = useCallback(async (fetchPage?: number): Promise<void> => {
    if (!token || !questionId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await adminApi.replies.list(token, questionId, {
        page: fetchPage ?? page,
        limit: 20,
      });

      // Guard: don't update state if unmounted or questionId changed mid-flight
      if (!isMountedRef.current || questionIdRef.current !== questionId) return;

      if (response.success) {
        repliesRef.current = response.data.replies;
        setReplies(response.data.replies);
        setPagination(response.data.pagination);
      } else {
        setError(getUserFriendlyMessage(response.error.code, response.error.message));
      }
    } catch {
      if (!isMountedRef.current) return;
      setError('Failed to load replies. Please try again.');
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [token, questionId, page]);

  // Fetch on mount and when page/questionId changes
  useEffect(() => {
    if (enabled) {
      if (solutionHydratedPageRef.current === page) {
        solutionHydratedPageRef.current = null;
        return;
      }
      fetchReplies();
    }
  }, [enabled, fetchReplies, page]);

  const refetch = useCallback(async (): Promise<void> => {
    await fetchReplies();
  }, [fetchReplies]);

  const approveReply = useCallback(async (replyId: string): Promise<void> => {
    if (pendingActions.has(replyId)) return;
    addPending(replyId);

    // Per-row rollback: capture only the affected reply
    const savedReply = captureReplyState(replies, replyId);

    // Optimistic update
    setReplies(prev =>
      prev.map(r =>
        r.replyId === replyId
          ? { ...r, moderationStatus: 'APPROVED' as ReplyModerationStatus }
          : r
      )
    );

    try {
      const response = await adminApi.replies.moderate(
        token, questionId, replyId, 'APPROVE'
      );

      if (!isMountedRef.current) return;

      if (response.success) {
        if (!response.data.applied) {
          // No-op — reconcile with server state
          setReplies(prev =>
            prev.map(r =>
              r.replyId === replyId
                ? { ...r, moderationStatus: response.data.reply.moderationStatus }
                : r
            )
          );
        }
        showSuccess(response.data.message);
      } else {
        // Per-row rollback
        if (savedReply) setReplies(prev => rollbackSingleReply(prev, savedReply));
        showError(getUserFriendlyMessage(response.error.code, response.error.message));
      }
    } catch {
      if (!isMountedRef.current) return;
      if (savedReply) setReplies(prev => rollbackSingleReply(prev, savedReply));
      showError('Failed to approve reply. Please try again.');
    } finally {
      if (isMountedRef.current) removePending(replyId);
    }
  }, [pendingActions, addPending, removePending, replies, token, questionId, showSuccess, showError]);

  const rejectReply = useCallback(async (replyId: string, reason: string): Promise<void> => {
    if (pendingActions.has(replyId)) return;
    addPending(replyId);

    const savedReply = captureReplyState(replies, replyId);

    // Optimistic update
    setReplies(prev =>
      prev.map(r =>
        r.replyId === replyId
          ? { ...r, moderationStatus: 'REJECTED' as ReplyModerationStatus }
          : r
      )
    );

    try {
      const response = await adminApi.replies.moderate(
        token, questionId, replyId, 'REJECT', reason.trim()
      );

      if (!isMountedRef.current) return;

      if (response.success) {
        if (!response.data.applied) {
          setReplies(prev =>
            prev.map(r =>
              r.replyId === replyId
                ? { ...r, moderationStatus: response.data.reply.moderationStatus }
                : r
            )
          );
        }
        showSuccess(response.data.message);
      } else {
        if (savedReply) setReplies(prev => rollbackSingleReply(prev, savedReply));
        showError(getUserFriendlyMessage(response.error.code, response.error.message));
      }
    } catch {
      if (!isMountedRef.current) return;
      if (savedReply) setReplies(prev => rollbackSingleReply(prev, savedReply));
      showError('Failed to reject reply. Please try again.');
    } finally {
      if (isMountedRef.current) removePending(replyId);
    }
  }, [pendingActions, addPending, removePending, replies, token, questionId, showSuccess, showError]);

  const hideReply = useCallback(async (replyId: string): Promise<void> => {
    if (pendingActions.has(replyId)) return;
    addPending(replyId);

    const savedReply = captureReplyState(replies, replyId);

    // Optimistic update
    setReplies(prev =>
      prev.map(r =>
        r.replyId === replyId
          ? { ...r, moderationStatus: 'HIDDEN' as ReplyModerationStatus, isSolution: false }
          : r
      )
    );

    try {
      const response = await adminApi.replies.moderate(
        token, questionId, replyId, 'HIDE'
      );

      if (!isMountedRef.current) return;

      if (response.success) {
        if (!response.data.applied) {
          setReplies(prev =>
            prev.map(r =>
              r.replyId === replyId
                ? { ...r, moderationStatus: response.data.reply.moderationStatus }
                : r
            )
          );
        }
        showSuccess(response.data.message);
      } else {
        if (savedReply) setReplies(prev => rollbackSingleReply(prev, savedReply));
        showError(getUserFriendlyMessage(response.error.code, response.error.message));
      }
    } catch {
      if (!isMountedRef.current) return;
      if (savedReply) setReplies(prev => rollbackSingleReply(prev, savedReply));
      showError('Failed to hide reply. Please try again.');
    } finally {
      if (isMountedRef.current) removePending(replyId);
    }
  }, [pendingActions, addPending, removePending, replies, token, questionId, showSuccess, showError]);

  /**
   * Execute one solution mutation while the caller owns the synchronous
   * panel-wide slot. Conflict/ambiguous failures roll back only solution flags;
   * moderation text, drafts, images, pagination, and the surrounding panel stay
   * in place.
   */
  const performSolutionWrite = useCallback(
    async (
      action: 'mark' | 'unmark',
      replyId: string,
      expectedStatus: QuestionStatus | null,
      sourceIssue?: SolutionWriteIssue
    ): Promise<void> => {
      const requestQuestionId = questionId;
      const targetStatus: QuestionStatus = action === 'mark' ? 'RESOLVED' : 'OPEN';
      const snapshot = captureSolutionState(repliesRef.current);
      const optimisticReplies =
        action === 'mark'
          ? repliesRef.current.map((reply) => ({
              ...reply,
              isSolution: reply.replyId === replyId,
            }))
          : repliesRef.current.map((reply) =>
              reply.replyId === replyId ? { ...reply, isSolution: false } : reply
            );
      repliesRef.current = optimisticReplies;
      setReplies(optimisticReplies);

      const rollback = (): void => {
        setReplies((previous) => {
          const rolledBack = rollbackSolutionState(previous, snapshot);
          repliesRef.current = rolledBack;
          return rolledBack;
        });
      };
      const inheritedAnnouncement =
        sourceIssue?.announcementKind ?? sourceIssue?.kind;

      try {
        const response =
          action === 'mark'
            ? await adminApi.replies.markSolution(
                token,
                requestQuestionId,
                replyId,
                expectedStatus
                  ? { expectedState: { status: expectedStatus } }
                  : undefined
              )
            : await adminApi.replies.unmarkSolution(token, requestQuestionId, replyId);

        if (
          !isMountedRef.current ||
          questionIdRef.current !== requestQuestionId
        ) {
          return;
        }

        if (response.success) {
          solutionStatusBaselineRef.current = targetStatus;
          setSolutionWriteIssue(null);
          showSuccess(action === 'mark' ? 'Reply marked as solution' : 'Solution removed');
          await fetchReplies();
          return;
        }

        rollback();
        const conflict = readQuestionSolutionConflict(response.error);
        if (conflict) {
          solutionStatusBaselineRef.current = conflict.observed.status;
          // Unmark has a hidden server-held resolutionReason predicate. After
          // one refresh/review and one explicit reapply, a second guarded loss
          // cannot be made safer by repeating the same status-only cycle.
          // Stop at repair-required instead of offering an unbounded
          // review→reapply→409 loop (for example, MANUAL_ADMIN resolution).
          if (action === 'unmark' && sourceIssue?.kind === 'ready') {
            publishSolutionWriteIssue({
              kind: 'repair_required',
              action,
              replyId,
              expectedStatus: conflict.expected.status,
              observedStatus: conflict.observed.status,
              targetStatus,
              eventId: sourceIssue.eventId,
              announcementKind: inheritedAnnouncement,
            });
          } else {
            publishSolutionWriteIssue({
              kind: 'conflict',
              action,
              replyId,
              expectedStatus: conflict.expected.status,
              observedStatus: conflict.observed.status,
              targetStatus,
              eventId: sourceIssue?.eventId,
              announcementKind: inheritedAnnouncement,
            });
          }
        } else if (response.error.code === 'STALE_WRITE') {
          publishSolutionWriteIssue({
            kind: 'refresh_required',
            action,
            replyId,
            ...(expectedStatus ? { expectedStatus } : {}),
            targetStatus,
            eventId: sourceIssue?.eventId,
            announcementKind: inheritedAnnouncement,
          });
        } else if (isAmbiguousSolutionFailure(response.error)) {
          publishSolutionWriteIssue({
            kind: 'ambiguous',
            action,
            replyId,
            ...(expectedStatus ? { expectedStatus } : {}),
            targetStatus,
            eventId: sourceIssue?.eventId,
            announcementKind: inheritedAnnouncement,
          });
        } else {
          setSolutionWriteIssue(null);
          showError(getUserFriendlyMessage(response.error.code, response.error.message));
        }
      } catch {
        if (
          !isMountedRef.current ||
          questionIdRef.current !== requestQuestionId
        ) {
          return;
        }
        rollback();
        publishSolutionWriteIssue({
          kind: 'ambiguous',
          action,
          replyId,
          ...(expectedStatus ? { expectedStatus } : {}),
          targetStatus,
          eventId: sourceIssue?.eventId,
          announcementKind: inheritedAnnouncement,
        });
      }
    },
    [
      fetchReplies,
      publishSolutionWriteIssue,
      questionId,
      showError,
      showSuccess,
      token,
    ]
  );

  const runSolutionWrite = useCallback(
    async (action: 'mark' | 'unmark', replyId: string): Promise<void> => {
      if (!claimSolutionAction(replyId)) return;
      setSolutionWriteIssue(null);
      try {
        await performSolutionWrite(
          action,
          replyId,
          solutionStatusBaselineRef.current
        );
      } finally {
        releaseSolutionAction(replyId);
      }
    },
    [claimSolutionAction, performSolutionWrite, releaseSolutionAction]
  );

  const markSolution = useCallback(
    async (replyId: string): Promise<void> => {
      await runSolutionWrite('mark', replyId);
    },
    [runSolutionWrite]
  );

  const unmarkSolution = useCallback(
    async (replyId: string): Promise<void> => {
      await runSolutionWrite('unmark', replyId);
    },
    [runSolutionWrite]
  );

  /**
   * Refresh the two pieces needed to prove full convergence: question status
   * and the target reply flag. `RESOLVED` alone is intentionally insufficient
   * because the CAS→flag-batch crash window can leave no flagged reply.
   */
  const refreshSolutionContext = useCallback(
    async (
      replyId: string
    ): Promise<{ status: QuestionStatus; isSolution: boolean } | null> => {
      const requestQuestionId = questionId;
      try {
        const [questionResponse, repliesResponse] = await Promise.all([
          adminApi.questions.getById(token, requestQuestionId),
          adminApi.replies.list(token, requestQuestionId, { page, limit: 20 }),
        ]);
        if (
          !isMountedRef.current ||
          questionIdRef.current !== requestQuestionId
        ) {
          return null;
        }
        const refreshedStatus = questionResponse.success
          ? readQuestionStatus(questionResponse.data.question.status)
          : null;
        let contextRepliesResponse = repliesResponse;
        let contextPage = page;
        let targetReply = repliesResponse.success
          ? repliesResponse.data.replies.find((reply) => reply.replyId === replyId)
          : undefined;
        if (repliesResponse.success && !targetReply) {
          // The reply was visible when the action began, but offset pagination
          // can move a boundary row when another staff member inserts or
          // removes a reply before recovery. Probe only the adjacent pages:
          // this closes the ordinary one-boundary shift without turning one
          // explicit recovery action into an unbounded list crawl.
          const totalPages = repliesResponse.data.pagination.totalPages;
          const adjacentPages = [page + 1, page - 1].filter(
            (candidate, index, candidates) =>
              candidate >= 1 &&
              candidate <= totalPages &&
              candidate !== page &&
              candidates.indexOf(candidate) === index
          );
          for (const adjacentPage of adjacentPages) {
            const adjacentResponse = await adminApi.replies.list(
              token,
              requestQuestionId,
              { page: adjacentPage, limit: 20 }
            );
            if (
              !isMountedRef.current ||
              questionIdRef.current !== requestQuestionId
            ) {
              return null;
            }
            if (!adjacentResponse.success) continue;
            targetReply = adjacentResponse.data.replies.find(
              (reply) => reply.replyId === replyId
            );
            if (targetReply) {
              contextRepliesResponse = adjacentResponse;
              contextPage = adjacentPage;
              break;
            }
          }
        }
        if (!refreshedStatus || !contextRepliesResponse.success || !targetReply) {
          showError('Could not refresh the current solution state. Try again.');
          return null;
        }

        solutionStatusBaselineRef.current = refreshedStatus;
        repliesRef.current = contextRepliesResponse.data.replies;
        setReplies(contextRepliesResponse.data.replies);
        setPagination(contextRepliesResponse.data.pagination);
        if (contextPage !== page) {
          solutionHydratedPageRef.current = contextPage;
          setPage(contextPage);
        }
        return { status: refreshedStatus, isSolution: targetReply.isSolution };
      } catch {
        if (isMountedRef.current) {
          showError('Could not refresh the current solution state. Try again.');
        }
        return null;
      }
    },
    [page, questionId, showError, token]
  );

  const recoverSolutionWrite = useCallback(async (): Promise<void> => {
    const issue = solutionWriteIssue;
    if (
      !issue ||
      issue.kind === 'converged' ||
      issue.kind === 'repair_required' ||
      !claimSolutionAction(issue.replyId)
    ) {
      return;
    }

    try {
      let refreshed:
        | { status: QuestionStatus; isSolution: boolean }
        | null = null;
      const observedStatus =
        'observedStatus' in issue ? issue.observedStatus : undefined;
      const statusAlreadyAtTarget = observedStatus === issue.targetStatus;

      // A status-only 409 is directly reusable except when it appears
      // converged: then the reply flag must be refreshed before claiming
      // "no action needed." Malformed conflicts and ambiguous outcomes also
      // require a refresh, for safe decoding and commit confirmation.
      if (
        issue.kind === 'ambiguous' ||
        issue.kind === 'refresh_required' ||
        (issue.action === 'unmark' && issue.kind !== 'ready') ||
        (issue.kind === 'conflict' && statusAlreadyAtTarget)
      ) {
        refreshed = await refreshSolutionContext(issue.replyId);
        if (!refreshed) return;

        const intendedFlag = issue.action === 'mark';
        if (
          refreshed.status === issue.targetStatus &&
          refreshed.isSolution === intendedFlag
        ) {
          publishSolutionWriteIssue({
            kind: 'converged',
            action: issue.action,
            replyId: issue.replyId,
            expectedStatus: issue.expectedStatus,
            observedStatus: refreshed.status,
            targetStatus: issue.targetStatus,
            eventId: issue.eventId,
            announcementKind: issue.announcementKind ?? issue.kind,
          });
          return;
        }

        // Unmark's hidden server predicate also includes resolutionReason.
        // A status-only conflict cannot prove that a replay is eligible, and
        // OPEN + still-flagged cannot converge through the DELETE route.
        // Refresh/review once. A still-RESOLVED flagged reply gets an explicit
        // second-step reapply; other inconsistent states stop for repair.
        if (issue.action === 'unmark') {
          const reviewedKind =
            refreshed.status === 'RESOLVED' && refreshed.isSolution
              ? 'ready'
              : 'repair_required';
          publishSolutionWriteIssue({
            kind: reviewedKind,
            action: issue.action,
            replyId: issue.replyId,
            expectedStatus: issue.expectedStatus,
            observedStatus: refreshed.status,
            targetStatus: issue.targetStatus,
            eventId: issue.eventId,
            announcementKind: issue.announcementKind ?? issue.kind,
          });
          return;
        }

        // An ambiguous mutation is confirmed before any replay. If it did not
        // converge, preserve the now-refreshed baseline and let the operator
        // explicitly reapply in a second action.
        if (issue.kind === 'ambiguous') {
          publishSolutionWriteIssue({
            kind: 'ready',
            action: issue.action,
            replyId: issue.replyId,
            expectedStatus: issue.expectedStatus,
            observedStatus: refreshed.status,
            targetStatus: issue.targetStatus,
            eventId: issue.eventId,
            announcementKind: issue.announcementKind ?? issue.kind,
          });
          return;
        }
      }

      const baseline =
        refreshed?.status ??
        ('observedStatus' in issue
          ? issue.observedStatus
          : solutionStatusBaselineRef.current);
      await performSolutionWrite(
        issue.action,
        issue.replyId,
        baseline,
        issue
      );
    } finally {
      releaseSolutionAction(issue.replyId);
    }
  }, [
    claimSolutionAction,
    performSolutionWrite,
    publishSolutionWriteIssue,
    refreshSolutionContext,
    releaseSolutionAction,
    solutionWriteIssue,
  ]);

  const createStaffReply = useCallback(async (body: string): Promise<boolean> => {
    // Synchronous in-flight guard: reject a second invocation that races the
    // first within the same tick (the ref flips synchronously, unlike state).
    if (isCreatingStaffReplyRef.current) return false;

    // Client-side validation is UX-only; the backend remains authoritative.
    // Trim before validating/submitting so whitespace-only bodies are rejected
    // and the server receives the same trimmed value the user sees validated.
    // Validation runs BEFORE the guard is set so an invalid body does not lock
    // the composer and can be corrected and retried.
    const trimmed = body.trim();
    if (
      trimmed.length < CONTENT_VALIDATION.MIN_REPLY_LENGTH ||
      trimmed.length > CONTENT_VALIDATION.MAX_REPLY_LENGTH
    ) {
      showError(
        `Reply must be between ${CONTENT_VALIDATION.MIN_REPLY_LENGTH} and ${CONTENT_VALIDATION.MAX_REPLY_LENGTH.toLocaleString()} characters.`
      );
      return false;
    }

    // Claim the in-flight slot synchronously, then mirror it into state for UI.
    isCreatingStaffReplyRef.current = true;
    setIsCreatingStaffReply(true);
    try {
      const response = await adminApi.replies.createStaffReply(token, questionId, trimmed);

      if (!isMountedRef.current) return false;

      if (response.success) {
        showSuccess(response.data.message ?? 'Staff reply created and published.');
        // PR4b: surface the created replyId (previously discarded) so the
        // panel can offer the post-create image-attach affordance. Guarded —
        // the response is server-shaped and the callback is optional.
        const createdReplyId = response.data.reply?.replyId;
        if (createdReplyId) {
          onCreated?.(createdReplyId);
        }
        // Reconcile from server truth — the create response is intentionally
        // narrow and cannot faithfully build an AdminReplyListItem, so refetch
        // through the existing safe path rather than synthesizing a local row.
        // Pagination is intentionally NOT reset here (see PR notes: a reply
        // posted while viewing page 2+ may land on page 1).
        await fetchReplies();
        return true;
      }

      // FORBIDDEN is a shared reply-action code; surface the staff-create-
      // specific message here rather than the generic shared default.
      const message =
        response.error.code === 'FORBIDDEN'
          ? STAFF_REPLY_FORBIDDEN_MESSAGE
          : getUserFriendlyMessage(response.error.code, response.error.message);
      showError(message);
      return false;
    } catch {
      if (!isMountedRef.current) return false;
      showError('Failed to post staff reply. Please try again.');
      return false;
    } finally {
      // Always release the synchronous guard; reset UI state only if mounted.
      isCreatingStaffReplyRef.current = false;
      if (isMountedRef.current) setIsCreatingStaffReply(false);
    }
  }, [token, questionId, showSuccess, showError, fetchReplies, onCreated]);

  return {
    replies,
    pagination,
    isLoading,
    error,
    pendingActions,
    isCreatingStaffReply,
    isSolutionActionInFlight,
    solutionPendingReplyId,
    solutionWriteIssue,
    page,
    setPage,
    refetch,
    createStaffReply,
    approveReply,
    rejectReply,
    hideReply,
    markSolution,
    unmarkSolution,
    recoverSolutionWrite,
    dismissSolutionWriteIssue,
  };
}
